import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Laptop,
  Coffee,
  Monitor,
  Sofa,
  Users,
  GraduationCap,
  Check,
  Heart,
} from 'lucide-react';
import SubpageShell from './SubpageShell';
import SEO from '../components/SEO';
import '../styles/Lounge.css';

/* ---------- 6-icon feature cards ---------- */
/* Each card's `desc` is a plain React node so canonical terms can deep-link
   to their glossary anchor on /language. Boosts SEO by driving link equity
   from a high-authority page (/families) into the definitions hub. */
const FEATURES = [
  {
    icon: Laptop,
    color: '#C77DFF',
    label: 'Neuro-Inclusive Workspace',
    desc: (
      <>
        Quiet zones and high-speed Wi-Fi within our{' '}
        <Link to="/knowledge#parent-wellbeing-infrastructure" className="lounge-feature-link">
          Parent Wellbeing Infrastructure
        </Link>{' '}
        help parents work, study and stay productive while remaining connected
        to their{' '}
        <Link to="/knowledge#neurodivergent-family-experiences" className="lounge-feature-link">
          Neurodivergent Family Experience
        </Link>
        .
      </>
    ),
  },
  {
    icon: Coffee,
    color: '#FFE066',
    label: 'Family Regulation Space',
    desc: (
      <>
        Comfortable{' '}
        <Link to="/knowledge#sensory-aware-environments" className="lounge-feature-link">
          sensory-aware environments
        </Link>{' '}
        designed to support regulation, wellbeing and emotional balance as
        part of our{' '}
        <Link to="/knowledge#neuro-inclusive-family-centre" className="lounge-feature-link">
          Neuro-Inclusive Family Infrastructure
        </Link>{' '}
        model.
      </>
    ),
  },
  {
    icon: Monitor,
    color: '#5BE9FF',
    label: 'Focused Access Pods',
    desc: (
      <>
        Private work pods supporting{' '}
        <Link to="/knowledge#family-access-infrastructure" className="lounge-feature-link">
          Family Access Infrastructure
        </Link>{' '}
        through quiet calls, remote work, meetings and structured productivity.
      </>
    ),
  },
  {
    icon: Sofa,
    color: '#FF6BC2',
    label: 'Sensory Recovery Zone',
    desc: (
      <>
        Low-stimulation spaces supporting sensory predictability, regulation
        and wellbeing for parents, carers and families.
      </>
    ),
  },
  {
    icon: Users,
    color: '#38BDF8',
    label: 'Community Connection',
    desc: (
      <>
        Build meaningful relationships through inclusive communities, family
        participation and shared{' '}
        <Link to="/knowledge#neurodivergent-family-experiences" className="lounge-feature-link">
          Neurodivergent Family Experiences
        </Link>
        .
      </>
    ),
  },
  {
    icon: GraduationCap,
    color: '#C77DFF',
    label: 'SEND Family Support',
    desc: (
      <>
        Regular workshops covering{' '}
        <Link to="/knowledge#send-family-support" className="lounge-feature-link">
          SEND Family Support
        </Link>
        , regulation strategies, sensory understanding,{' '}
        <Link to="/knowledge#neuro-inclusive-play" className="lounge-feature-link">
          Neuro-Inclusive Play
        </Link>{' '}
        and family wellbeing.
      </>
    ),
  },
];

const CHECKLIST = [
  'A calm environment',
  'Designed for comfort',
  'Supporting your wellbeing',
  'Flexible for you',
  'Because you matter too',
];

const Families = () => {
  const navigate = useNavigate();

  return (
    <SubpageShell currentPath="/families">
      <SEO
        title="Parent Base"
        description="The integrated Parent Base inside every N3URO WORLD® environment — designed so parents stay physically present while children move through structured Journey Flows."
        path="/families"
        noindex
      />
      <main className="lounge-page" data-testid="parents-lounge">
        {/* ============= HERO ROW ============= */}
        <section className="lounge-hero-row">
          <div className="lounge-hero-text">
            <span className="lounge-eyebrow">Parent Base</span>
            <h1 className="lounge-title">
              A space for{' '}
              <span className="lounge-hl lounge-hl-cyan">you</span>.
              <br />
              As well as{' '}
              <span className="lounge-hl lounge-hl-mag">them</span>.
            </h1>
            <p className="lounge-subtitle">Support &middot; Connection &middot; Balance</p>
            <figure
              className="lounge-hero-photo"
              data-testid="lounge-hero-photo"
              aria-label="N3URO WORLD® Parent Base interior render"
            >
              <img width="1536" height="1024" decoding="async" fetchPriority="high"
                src="/img/families-hero.webp"
                alt="N3URO WORLD® Parent Base — neuro-inclusive family infrastructure with a calm parent café, work pods and colourful play experiences behind glass"
                loading="eager"
              />
              <div className="lounge-hero-glow" aria-hidden="true" />
            </figure>
            <p className="lounge-desc">
              The N3URO WORLD® Parent Base is designed for parents and carers
              &mdash; a calm, welcoming space where you can work, relax, connect
              and be part of a community that gets it.
            </p>
          </div>
        </section>

        {/* ============= 6-ICON FEATURE STRIP ============= */}
        <section className="lounge-features" data-testid="lounge-features">
          {FEATURES.map(({ icon: Icon, color, label, desc }) => (
            <article key={label} className="lounge-feature">
              <span
                className="lounge-feature-icon"
                style={{ borderColor: color, color }}
                aria-hidden="true"
              >
                <Icon size={26} strokeWidth={2} />
              </span>
              <h3 className="lounge-feature-label" style={{ color }}>
                {label}
              </h3>
              <p className="lounge-feature-desc">{desc}</p>
            </article>
          ))}
        </section>

        {/* ============= SEO CANONICAL PARAGRAPH =============
             Cross-linked to the /language glossary so Google crawls the
             canonical brand terms from this high-authority page. */}
        <section className="lounge-seo" data-testid="lounge-seo">
          <h2 className="lounge-seo-title">
            <span className="lounge-hl lounge-hl-cyan">A space for you.</span>{' '}
            <span className="lounge-hl lounge-hl-mag">As well as them.</span>
          </h2>
          <p className="lounge-seo-body">
            The N3URO WORLD® Parent Base forms part of our wider{' '}
            <Link to="/knowledge#parent-wellbeing-infrastructure" className="lounge-seo-term">
              Parent Wellbeing Infrastructure
            </Link>{' '}
            and{' '}
            <Link to="/knowledge#family-access-infrastructure" className="lounge-seo-term">
              Family Access Infrastructure
            </Link>{' '}
            model. Designed as a calm, welcoming environment, it provides
            opportunities for work, relaxation, community connection and family
            participation. Through{' '}
            <Link to="/knowledge#sensory-aware-environments" className="lounge-seo-term">
              sensory-aware environments
            </Link>
            ,{' '}
            <Link to="/knowledge#family-regulation-spaces" className="lounge-seo-term">
              Family Regulation Spaces
            </Link>{' '}
            and inclusive community experiences, parents and carers can
            recharge, connect and remain actively involved in their
            child&rsquo;s{' '}
            <Link to="/knowledge#neurodivergent-family-experiences" className="lounge-seo-term">
              Neurodivergent Family Experience
            </Link>{' '}
            while enjoying the benefits of a truly{' '}
            <Link to="/knowledge#neuro-inclusive-family-centre" className="lounge-seo-term">
              Neuro-Inclusive family centre
            </Link>
            .
          </p>
        </section>

        {/* ============= 2-COLUMN: COMMUNITY + CHECKLIST ============= */}
        <section className="lounge-twocol" data-testid="lounge-twocol">
          <article className="lounge-card lounge-card-community">
            <h2 className="lounge-card-title">
              <span className="lounge-hl lounge-hl-mag">More than a lounge.</span>
              <br />
              <span className="lounge-hl lounge-hl-cyan">A community.</span>
            </h2>
            <p>You&rsquo;re not just visiting. You&rsquo;re part of something.</p>
            <p>
              A supportive community where parents and carers can connect, share
              and grow together.
            </p>
          </article>

          <article className="lounge-card lounge-card-built">
            <h2 className="lounge-card-title">
              <span className="lounge-hl lounge-hl-mag">Built for parents.</span>
              <br />
              <span className="lounge-hl lounge-hl-cyan">Designed for real life.</span>
            </h2>
            <ul className="lounge-checklist">
              {CHECKLIST.map((item) => (
                <li key={item}>
                  <span className="lounge-check-icon" aria-hidden="true">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </section>

        {/* ============= THRIVE BANNER ============= */}
        <section className="lounge-thrive" data-testid="lounge-thrive">
          <div className="lounge-thrive-icon" aria-hidden="true">
            <Heart size={26} strokeWidth={2} />
          </div>
          <div className="lounge-thrive-text">
            <p className="lounge-thrive-line1">
              <span className="lounge-hl lounge-hl-cyan">When they thrive,</span>{' '}
              <span className="lounge-hl lounge-hl-mag">you thrive.</span>
            </p>
            <p className="lounge-thrive-line2">
              Together, we build stronger families and stronger communities.
            </p>
          </div>
        </section>

        {/* ============= FINAL TAGLINE ============= */}
        <p className="lounge-final">
          <span className="lounge-hl lounge-hl-cyan">Supporting you.</span>
          <span className="lounge-final-dot">&middot;</span>
          <span className="lounge-hl lounge-hl-mag">Connecting you.</span>
          <span className="lounge-final-dot">&middot;</span>
          <span className="lounge-hl lounge-hl-yellow">Empowering you.</span>
        </p>

        {/* ============= CTAs ============= */}
        <div className="lounge-ctas">
          <a
            href="mailto:hello@n3uro.world"
            className="lounge-cta lounge-cta-primary"
            data-testid="lounge-parent-cta"
          >
            Register interest as a parent
          </a>
          <a
            href="mailto:hello@n3uro.world"
            className="lounge-cta lounge-cta-ghost"
            data-testid="lounge-contact-cta"
          >
            Contact us
          </a>
        </div>
      </main>
    </SubpageShell>
  );
};

export default Families;
